/**
 * Meta Pixel bridge: converts dataLayer ecommerce events → fbq('track', …).
 * Assumes Pixel is initialized once by GTM-TRHRLXPH (fbq init + PageView).
 * Never loads fbevents.js and never calls fbq('init').
 */

import axiosInstance from "../store/request";
import {
  META_CUSTOM_EVENTS,
  META_EVENT_MAP,
  META_PIXEL_ID,
  toNumber,
} from "./dataLayer";

const CAPI_EVENTS = new Set([
  "Purchase",
  "Lead",
  "CompleteRegistration",
  "Contact",
]);

const firedBrowser = new Set();
let pending = [];
let fbqReady = null;

function canUseDom() {
  return typeof window !== "undefined";
}

function readCookie(name) {
  try {
    const m = document.cookie.match(
      new RegExp(
        "(?:^|; )" + name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1") + "=([^;]*)"
      )
    );
    return m ? decodeURIComponent(m[1]) : null;
  } catch {
    return null;
  }
}

/**
 * Guard fbq so:
 * - fbq('init', PIXEL) runs only once (blocks SPA/GTM re-init → conflicting versions)
 * - ViewCart never uses fbq('track', …) — rewritten to trackCustom
 */
function installFbqInitGuard() {
  if (!canUseDom() || window.__vlvFbqInitGuardInstalled) return;
  window.__vlvFbqInitGuardInstalled = true;
  window.__vlvFbqInitedIds = window.__vlvFbqInitedIds || new Set();

  const wrap = () => {
    const current = window.fbq;
    if (typeof current !== "function") return false;
    if (current.__vlvInitGuarded) return true;

    try {
      const st = current.getState && current.getState();
      (st?.pixels || []).forEach((p) => {
        if (p?.id) window.__vlvFbqInitedIds.add(String(p.id));
      });
    } catch {
      /* ignore */
    }

    function guardedFbq() {
      const cmd = arguments[0];
      if (cmd === "init") {
        const id = String(arguments[1] || "");
        if (id && window.__vlvFbqInitedIds.has(id)) {
          return; // duplicate init — ignore
        }
        if (id) window.__vlvFbqInitedIds.add(id);
      }
      // Rewrite non-standard ViewCart track → trackCustom (GTM or legacy tags).
      if (cmd === "track" && arguments[1] === "ViewCart") {
        const args = Array.prototype.slice.call(arguments);
        args[0] = "trackCustom";
        return current.apply(this, args);
      }
      return current.apply(this, arguments);
    }

    try {
      Object.keys(current).forEach((k) => {
        guardedFbq[k] = current[k];
      });
    } catch {
      /* ignore */
    }
    guardedFbq.__vlvInitGuarded = true;
    if (typeof current.push === "function") {
      guardedFbq.push = current.push.bind(current);
    }
    if (current.queue) guardedFbq.queue = current.queue;
    if (current.loaded != null) guardedFbq.loaded = current.loaded;
    if (current.version) guardedFbq.version = current.version;
    if (typeof current.callMethod === "function") {
      guardedFbq.callMethod = current.callMethod.bind(current);
    }
    if (typeof current.getState === "function") {
      guardedFbq.getState = current.getState.bind(current);
    }

    window.fbq = guardedFbq;
    if (window._fbq === current) window._fbq = guardedFbq;
    return true;
  };

  if (!wrap()) {
    const t = setInterval(() => {
      if (wrap()) clearInterval(t);
    }, 50);
    setTimeout(() => clearInterval(t), 20000);
  } else {
    // fbevents.js may replace window.fbq after stub — re-wrap briefly.
    const t = setInterval(() => {
      wrap();
    }, 200);
    setTimeout(() => clearInterval(t), 15000);
  }
}

function ensureFbq() {
  if (fbqReady) return fbqReady;
  installFbqInitGuard();
  fbqReady = new Promise((resolve) => {
    if (typeof window.fbq === "function") {
      installFbqInitGuard();
      resolve(window.fbq);
      return;
    }
    const start = Date.now();
    const t = setInterval(() => {
      if (typeof window.fbq === "function") {
        clearInterval(t);
        installFbqInitGuard();
        resolve(window.fbq);
      } else if (Date.now() - start > 10000) {
        clearInterval(t);
        resolve(null);
      }
    }, 100);
  });
  return fbqReady;
}

function metaPayloadFromDl(entry) {
  const ecom = entry.ecommerce || {};
  const items = Array.isArray(ecom.items) ? ecom.items : [];

  const content_ids =
    Array.isArray(entry.content_ids) && entry.content_ids.length
      ? entry.content_ids.map(String)
      : items.map((it) => String(it.item_id)).filter(Boolean);

  const contents =
    Array.isArray(entry.contents) && entry.contents.length
      ? entry.contents
      : items.map((it) => ({
          id: String(it.item_id),
          quantity: Math.max(1, toNumber(it.quantity) || 1),
          item_price: toNumber(it.price),
        }));

  const value =
    entry.value != null
      ? toNumber(entry.value)
      : ecom.value != null
        ? toNumber(ecom.value)
        : undefined;

  const payload = {};
  if (content_ids.length) {
    payload.content_ids = content_ids;
    payload.content_type = "product";
    payload.contents = contents;
  }
  if (value != null && Number.isFinite(value)) payload.value = value;
  payload.currency = entry.currency || ecom.currency || "AMD";

  const search = entry.search_string || entry.search_term;
  if (search) payload.search_string = String(search);

  return payload;
}

async function sendCapi({ event_name, event_id, custom_data }) {
  if (!CAPI_EVENTS.has(event_name) || event_name === "Purchase") return;
  try {
    await axiosInstance.post("meta/capi", {
      token: process.env.REACT_APP_TOKEN,
      login: process.env.REACT_APP_LOGIN,
      password: process.env.REACT_APP_PASSWORD,
      event_name,
      event_id,
      custom_data,
      fbp: readCookie("_fbp"),
      fbc: readCookie("_fbc"),
      event_source_url: window.location.href,
    });
  } catch {
    /* ignore */
  }
}

async function emitMeta(entry) {
  const metaName = entry.meta_event || META_EVENT_MAP[entry.event];
  if (!metaName) return;

  const eventID = entry.event_id ? String(entry.event_id) : "";
  const dedupeKey = eventID || `${metaName}:${JSON.stringify(entry.event)}:${Date.now()}`;
  if (eventID && firedBrowser.has(eventID)) return;
  if (eventID) firedBrowser.add(eventID);

  const payload = metaPayloadFromDl(entry);
  const fbq = await ensureFbq();
  if (typeof fbq === "function") {
    try {
      // Never fbq('init') here — GTM owns Pixel init for 958443791456238.
      // ViewCart is custom-only (never fbq('track', 'ViewCart', …)).
      const useCustom =
        metaName === "ViewCart" || META_CUSTOM_EVENTS.has(metaName);
      const method = useCustom ? "trackCustom" : "track";
      if (eventID) fbq(method, metaName, payload, { eventID });
      else fbq(method, metaName, payload);
      if (typeof console !== "undefined" && console.info) {
        console.info("[metaBridge]", metaName, {
          method,
          eventID,
          payload,
          pixel: META_PIXEL_ID,
        });
      }
    } catch {
      /* ignore */
    }
  }

  if (eventID && CAPI_EVENTS.has(metaName) && metaName !== "Purchase") {
    sendCapi({
      event_name: metaName,
      event_id: eventID,
      custom_data: payload,
    });
  }

  void dedupeKey;
}

function enqueue(entry) {
  if (!entry || typeof entry !== "object") return;
  if (entry.ecommerce === null && !entry.event) return;
  if (!entry.event) return;
  if (!(entry.meta_event || META_EVENT_MAP[entry.event])) return;
  pending.push(entry);
  flush();
}

let flushing = false;
async function flush() {
  if (flushing) return;
  flushing = true;
  try {
    while (pending.length) {
      const entry = pending.shift();
      await emitMeta(entry);
    }
  } finally {
    flushing = false;
  }
}

export function initMetaBridge() {
  if (!canUseDom()) return;
  if (window.__vlvMetaBridgeInit) return;
  window.__vlvMetaBridgeInit = true;

  // Install ASAP so SPA navigation / duplicate GTM tags cannot re-init the pixel.
  installFbqInitGuard();

  window.dataLayer = Array.isArray(window.dataLayer) ? window.dataLayer : [];
  const dl = window.dataLayer;
  const originalPush = dl.push.bind(dl);

  dl.push = function metaAwarePush() {
    const result = originalPush.apply(null, arguments);
    try {
      for (let i = 0; i < arguments.length; i += 1) {
        enqueue(arguments[i]);
      }
    } catch {
      /* ignore */
    }
    return result;
  };

  // Catch events pushed before bridge init (same page load).
  try {
    dl.forEach((entry) => enqueue(entry));
  } catch {
    /* ignore */
  }
}
